import { supabase, DEFAULT_ORG_ID, applyOrgFilter, getActiveOrgOverride, signImagesDeep } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { isValidCurp } from '../curp'
import { gateConsentimientoActivo } from './consent'

// =====================================================
// player_club_memberships
// =====================================================

export const getPlayerMemberships = async (jugadorId) => {
  try {
    let query = supabase
      .from('player_club_memberships')
      .select(`
        *,
        organizations:org_id (id, name, slug, logo_url, type)
      `)
      .eq('jugador_id', jugadorId)
      .order('es_principal', { ascending: false })
      .order('joined_at', { ascending: true })

    query = applyOrgFilter(query)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPlayerMemberships]', error)
    return { data: [], error }
  }
}

export const getPlayersByOrgMembership = async (orgId = null) => {
  try {
    let query = supabase
      .from('player_club_memberships')
      .select(`
        *,
        jugadores:jugador_id (id, nombre_completo, nickname, numero, imagen_path, imagen_url, estatus)
      `)
      .eq('status', 'active')

    if (orgId) query = query.eq('org_id', orgId)

    query = applyOrgFilter(query)

    const { data, error } = await query
    if (error) throw error
    await signImagesDeep(data)
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPlayersByOrgMembership]', error)
    return { data: [], error }
  }
}

export const getPlayerActiveClubs = async (jugadorId) => {
  try {
    const { data, error } = await supabase.rpc('get_player_active_clubs', {
      p_jugador_id: jugadorId
    })
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPlayerActiveClubs]', error)
    return { data: [], error }
  }
}

export const createPlayerMembership = async ({
  jugadorId,
  orgId,
  esPrincipal = false,
  status = 'active',
  joinedAt = null,
  dorsalPrincipal = null,
  posicionPrincipal = null,
  notas = null
}) => {
  try {
    if (status === 'active') {
      const { data: jug } = await supabase
        .from('jugadores').select('curp').eq('id', jugadorId).single()
      if (!isValidCurp(jug?.curp)) {
        return { data: null, error: new Error('Captura una CURP válida para activar al jugador.') }
      }
      // La membresia es la FUENTE DE VERDAD del estatus del jugador, asi que aqui
      // aplica el mismo requisito que en jugadores.estatus. Sin esto, transferir a
      // un jugador lo dejaba activo en el club destino sin consentimiento.
      const gate = await gateConsentimientoActivo({ orgId, jugadorId })
      if (!gate.ok) return { data: null, error: gate.error }
    }
    const { data: staffData } = await getCurrentStaff()
    const payload = {
      jugador_id: jugadorId,
      org_id: orgId || getActiveOrgOverride() || staffData?.org_id || DEFAULT_ORG_ID,
      es_principal: esPrincipal,
      status,
      joined_at: joinedAt || new Date().toISOString().slice(0, 10),
      dorsal_principal: dorsalPrincipal,
      posicion_principal: posicionPrincipal,
      notas,
      created_by: staffData?.id || null
    }
    const { data, error } = await supabase
      .from('player_club_memberships')
      .insert([payload])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createPlayerMembership]', error)
    return { data: null, error }
  }
}

export const updatePlayerMembership = async (membershipId, patch) => {
  try {
    if (patch?.status === 'active') {
      const { data: mem } = await supabase
        .from('player_club_memberships').select('jugador_id, org_id').eq('id', membershipId).single()
      const { data: jug } = await supabase
        .from('jugadores').select('curp').eq('id', mem?.jugador_id).single()
      if (!isValidCurp(jug?.curp)) {
        return { data: null, error: new Error('Captura una CURP válida para activar al jugador.') }
      }
      const gate = await gateConsentimientoActivo({ orgId: mem?.org_id, jugadorId: mem?.jugador_id })
      if (!gate.ok) return { data: null, error: gate.error }
    }
    const { data, error } = await supabase
      .from('player_club_memberships')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', membershipId)
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updatePlayerMembership]', error)
    return { data: null, error }
  }
}

export const closePlayerMembership = async (membershipId, leftAt = null) => {
  return updatePlayerMembership(membershipId, {
    status: 'inactive',
    left_at: leftAt || new Date().toISOString().slice(0, 10)
  })
}

export const transferPlayer = async ({ jugadorId, fromOrgId, toOrgId, notas = null }) => {
  try {
    const { data: existing, error: selErr } = await supabase
      .from('player_club_memberships')
      .select('id')
      .eq('jugador_id', jugadorId)
      .eq('org_id', fromOrgId)
      .eq('status', 'active')
      .maybeSingle()
    if (selErr) throw selErr

    if (existing?.id) {
      await updatePlayerMembership(existing.id, {
        status: 'transferred',
        left_at: new Date().toISOString().slice(0, 10)
      })
    }

    const result = await createPlayerMembership({
      jugadorId,
      orgId: toOrgId,
      esPrincipal: true,
      notas
    })
    return result
  } catch (error) {
    console.error('[transferPlayer]', error)
    return { data: null, error }
  }
}

// =====================================================
// staff_club_memberships
// =====================================================

export const getStaffMemberships = async (staffId) => {
  try {
    let query = supabase
      .from('staff_club_memberships')
      .select(`
        *,
        organizations:org_id (id, name, slug, logo_url, type)
      `)
      .eq('staff_id', staffId)
      .order('es_principal', { ascending: false })
      .order('joined_at', { ascending: true })

    query = applyOrgFilter(query)

    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getStaffMemberships]', error)
    return { data: [], error }
  }
}

export const createStaffMembership = async ({
  staffId,
  orgId,
  rol = 'coach',
  esPrincipal = false,
  status = 'active',
  notas = null
}) => {
  try {
    const { data: cur } = await getCurrentStaff()
    const payload = {
      staff_id: staffId,
      org_id: orgId || getActiveOrgOverride() || cur?.org_id || DEFAULT_ORG_ID,
      rol,
      es_principal: esPrincipal,
      status,
      joined_at: new Date().toISOString().slice(0, 10),
      notas,
      created_by: cur?.id || null
    }
    const { data, error } = await supabase
      .from('staff_club_memberships')
      .insert([payload])
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createStaffMembership]', error)
    return { data: null, error }
  }
}

export const updateStaffMembership = async (membershipId, patch) => {
  try {
    const { data, error } = await supabase
      .from('staff_club_memberships')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', membershipId)
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateStaffMembership]', error)
    return { data: null, error }
  }
}

// =====================================================
// Helpers cross-org (Platform Admin)
// =====================================================

export const getPlayersInMultipleClubs = async () => {
  try {
    const { data, error } = await supabase
      .from('player_club_memberships')
      .select(`
        jugador_id,
        org_id,
        es_principal,
        joined_at,
        status,
        organizations:org_id (id, name, logo_url),
        jugadores:jugador_id (id, nombre_completo, imagen_path, imagen_url)
      `)
      .eq('status', 'active')

    if (error) throw error

    await signImagesDeep(data)

    const byPlayer = new Map()
    ;(data || []).forEach((row) => {
      if (!byPlayer.has(row.jugador_id)) byPlayer.set(row.jugador_id, [])
      byPlayer.get(row.jugador_id).push(row)
    })

    const result = []
    byPlayer.forEach((rows, jugadorId) => {
      if (rows.length > 1) {
        result.push({
          jugadorId,
          jugador: rows[0].jugadores,
          memberships: rows
        })
      }
    })
    return { data: result, error: null }
  } catch (error) {
    console.error('[getPlayersInMultipleClubs]', error)
    return { data: [], error }
  }
}
